import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { firstValueFrom } from 'rxjs';

/**
 * Interface para items de prescripcion del backend
 */
export interface IPrescriptionItem {
  id: number;
  prescription_id: number;
  medication: string;
  dosage: string;
  frequency: string;
  via: string;
  duration: string;
  instructions: string;
  created_at: string;
  updated_at: string;
}

/**
 * Interface para prescripcion del backend
 */
export interface IPrescriptionResponse {
  id: number;
  patient_id: number;
  user_id: number;
  appointment_id: number | null;
  notes: string | null;
  modo_entrada?: 'formulario' | 'texto';
  contenido_html?: string;
  created_at: string;
  updated_at: string;
  doctor?: {
    id: number;
    name: string;
    last_name: string;
  };
  items: IPrescriptionItem[];
}

/**
 * Interface para receta del frontend (formato indicaciones)
 */
export interface IRecetaFrontend {
  id: string;
  tipo: 'receta';
  nombre: string;
  fecha_creacion: string;
  ultima_modificacion: string;
  finalizada: boolean;
  origen: string;
  modo_entrada?: 'formulario' | 'texto';
  contenido_html?: string;
  medicamentos: {
    nombre: string;
    dosis: string;
    frecuencia: string;
    via: string;
    duracion: string;
    indicaciones: string;
  }[];
  notas?: string;
  backendId?: number;
  esHistorico?: boolean;
}

/**
 * Servicio para gestion de recetas medicas (prescripciones)
 */
@Injectable({
  providedIn: 'root',
})
export class PrescriptionService {
  private readonly endpoint = 'patients';

  constructor(private apiService: ApiService) {}

  /**
   * Obtiene todas las recetas historicas de un paciente
   */
  async getByPatient(patientId: number | string): Promise<IPrescriptionResponse[]> {
    console.log('[PrescriptionService] Cargando recetas del paciente:', patientId);
    console.log('[PrescriptionService] Endpoint:', `${this.endpoint}/${patientId}/prescriptions`);

    try {
      const response = await firstValueFrom(
        this.apiService.get<{ data: IPrescriptionResponse[]; meta?: any; success?: boolean }>(
          `${this.endpoint}/${patientId}/prescriptions`
        )
      );

      console.log('[PrescriptionService] Response completa:', JSON.stringify(response, null, 2));
      console.log('[PrescriptionService] Recetas obtenidas:', response.data?.length || 0);

      if (response.data && response.data.length > 0) {
        console.log('[PrescriptionService] Primera receta:', response.data[0]);
      }

      return response.data || [];
    } catch (error: any) {
      console.error('[PrescriptionService] Error obteniendo recetas:', error);
      console.error('[PrescriptionService] Error status:', error?.status);
      console.error('[PrescriptionService] Error message:', error?.message);

      if (error?.status === 404) {
        console.log('[PrescriptionService] Paciente no tiene recetas (404)');
        return [];
      }

      // No lanzar error para evitar que falle el flujo
      console.warn('[PrescriptionService] Retornando array vacio por error');
      return [];
    }
  }

  /**
   * Obtiene la ultima receta del paciente
   */
  async getLatest(patientId: number | string): Promise<IPrescriptionResponse | null> {
    console.log('[PrescriptionService] Obteniendo ultima receta del paciente:', patientId);

    try {
      const response = await firstValueFrom(
        this.apiService.get<{ data: IPrescriptionResponse }>(
          `${this.endpoint}/${patientId}/prescriptions/latest`
        )
      );

      return response.data;
    } catch (error: any) {
      if (error?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Crea una nueva receta medica en el backend
   * Soporta tanto medicamentos estructurados como texto libre (contenido_html)
   */
  async create(
    patientId: number | string,
    items: { nombre: string; dosis?: string; frecuencia?: string; via?: string; duracion?: string; indicaciones?: string }[],
    notes?: string,
    appointmentId?: number | null,
    options?: {
      modoEntrada?: 'formulario' | 'texto';
      contenidoHtml?: string;
      medicalRecordId?: number;
    }
  ): Promise<IPrescriptionResponse> {
    console.log('[PrescriptionService] Creando receta para paciente:', patientId);
    console.log('[PrescriptionService] Modo entrada:', options?.modoEntrada || 'formulario');
    console.log('[PrescriptionService] Medical record ID:', options?.medicalRecordId);

    const payload: any = {
      patient_id: Number(patientId),
    };

    if (options?.medicalRecordId) {
      payload.medical_record_id = options.medicalRecordId;
    }

    if (options?.modoEntrada === 'texto' && options?.contenidoHtml) {
      payload.modo_entrada = 'texto';
      payload.contenido_html = options.contenidoHtml;
      payload.items = [{
        medication: 'Indicaciones en texto libre',
        dosage: '',
        frequency: '',
        duration: '',
        instructions: '',
      }];
    } else {
      const backendItems = items.map((item) => ({
        medication: item.nombre,
        dosage: item.dosis || '',
        frequency: item.frecuencia || '',
        via: item.via || '',
        duration: item.duracion || '',
        instructions: item.indicaciones || '',
      }));
      payload.items = backendItems;
      payload.modo_entrada = 'formulario';
    }

    if (notes) {
      payload.notes = notes;
    }

    if (appointmentId) {
      payload.appointment_id = appointmentId;
    }

    console.log('[PrescriptionService] Payload:', JSON.stringify(payload, null, 2));

    const response = await firstValueFrom(
      this.apiService.post<{ success: boolean; data: IPrescriptionResponse; message: string }>(
        'prescriptions',
        payload
      )
    );

    console.log('[PrescriptionService] Receta creada:', response.data);
    return response.data;
  }

  /**
   * Elimina una receta médica del backend
   */
  async delete(prescriptionId: number): Promise<void> {
    console.log('[PrescriptionService] Eliminando receta:', prescriptionId);

    await firstValueFrom(
      this.apiService.delete<{ success: boolean; message: string }>(
        `prescriptions/${prescriptionId}`
      )
    );

    console.log('[PrescriptionService] Receta eliminada exitosamente');
  }

  /**
   * Actualiza una receta médica existente
   */
  async update(
    prescriptionId: number,
    items?: { nombre: string; dosis?: string; frecuencia?: string; via?: string; duracion?: string; indicaciones?: string }[],
    notes?: string,
    options?: {
      modoEntrada?: 'formulario' | 'texto';
      contenidoHtml?: string;
    }
  ): Promise<IPrescriptionResponse> {
    console.log('[PrescriptionService] Actualizando receta:', prescriptionId);

    const payload: any = {};

    if (options?.modoEntrada === 'texto' && options?.contenidoHtml) {
      payload.modo_entrada = 'texto';
      payload.contenido_html = options.contenidoHtml;
    } else if (items && items.length > 0) {
      payload.items = items.map((item) => ({
        medication: item.nombre,
        dosage: item.dosis || '',
        frequency: item.frecuencia || '',
        via: item.via || '',
        duration: item.duracion || '',
        instructions: item.indicaciones || '',
      }));
      payload.modo_entrada = 'formulario';
    }

    if (notes !== undefined) {
      payload.notes = notes;
    }

    console.log('[PrescriptionService] Update payload:', JSON.stringify(payload, null, 2));

    const response = await firstValueFrom(
      this.apiService.put<{ success: boolean; data: IPrescriptionResponse; message: string }>(
        `prescriptions/${prescriptionId}`,
        payload
      )
    );

    console.log('[PrescriptionService] Receta actualizada:', response.data);
    return response.data;
  }

  /**
   * Convierte prescripciones del backend al formato del frontend
   */
  convertToFrontendFormat(prescriptions: IPrescriptionResponse[]): IRecetaFrontend[] {
    return prescriptions.map((p) => this.prescriptionToReceta(p));
  }

  /**
   * Convierte una prescripcion individual al formato del frontend
   */
  private prescriptionToReceta(prescription: IPrescriptionResponse): IRecetaFrontend {
    const fecha = new Date(prescription.created_at);
    const nombre = `R-${fecha.getDate()}/${fecha.getMonth() + 1}/${fecha.getFullYear()}`;

    console.log('[PrescriptionService] prescriptionToReceta - ID:', prescription.id);
    console.log('[PrescriptionService] prescriptionToReceta - modo_entrada:', prescription.modo_entrada);
    console.log('[PrescriptionService] prescriptionToReceta - items:', prescription.items?.length || 0);

    const medicamentos = (prescription.items || []).map((item) => ({
      nombre: item.medication || '',
      dosis: item.dosage || '',
      frecuencia: item.frequency || '',
      via: item.via || '',
      duracion: item.duration || '',
      indicaciones: item.instructions || '',
    }));

    console.log('[PrescriptionService] prescriptionToReceta - medicamentos convertidos:', medicamentos.length);

    return {
      id: `backend-${prescription.id}`,
      tipo: 'receta',
      nombre,
      fecha_creacion: prescription.created_at,
      ultima_modificacion: prescription.updated_at,
      finalizada: true,
      origen: 'historico',
      modo_entrada: prescription.modo_entrada || 'formulario',
      contenido_html: prescription.contenido_html || '',
      medicamentos,
      notas: prescription.notes || '',
      backendId: prescription.id,
      esHistorico: true,
    };
  }
}
